import type { UploadProcessingProgressUpdate } from '@lib/upload-processing/types/upload-processing-progress-update';

import { FlowDocumentProcessingError } from '../hooks/use-flow-document-processing';
import type { UploadFlowDocumentBatchItem } from '../hooks/use-upload-flow-document-v2';
import type { FlowDocumentProcessingRetryResult } from '../types/flow-document-processing-retry-result';

export interface FlowDocumentProcessingRetryTarget {
  flowId: string;
  collectionId: string;
}

export function readFlowDocumentProcessingRetryTargetFromProgress(
  update: Pick<UploadProcessingProgressUpdate, 'flowId' | 'collectionId'>
): FlowDocumentProcessingRetryTarget | null {
  if (!update.flowId || !update.collectionId) {
    return null;
  }
  return { flowId: update.flowId, collectionId: update.collectionId };
}

/** Shared uploadBatch listener for post-accept flow/collection ids (EB, templates, etc.). */
export function createFlowDocumentProcessingRetryProgressListener(
  onTarget: (target: FlowDocumentProcessingRetryTarget) => void
): (update: UploadProcessingProgressUpdate) => void {
  return update => {
    const target = readFlowDocumentProcessingRetryTargetFromProgress(update);
    if (target) {
      onTarget(target);
    }
  };
}

export type InvokeFlowDocumentProcessingRetryOrFallbackResult =
  | { path: 'reupload' }
  | {
      path: 'processing';
      result: FlowDocumentProcessingRetryResult | undefined;
    };

export async function invokeFlowDocumentProcessingRetryOrFallback(args: {
  target: FlowDocumentProcessingRetryTarget | null | undefined;
  retryDocumentProcessing: (
    target: FlowDocumentProcessingRetryTarget
  ) => Promise<FlowDocumentProcessingRetryResult | undefined>;
  onBeforeProcessingRetry?: () => void;
  onReupload: () => void;
}): Promise<InvokeFlowDocumentProcessingRetryOrFallbackResult> {
  if (hasFlowDocumentProcessingRetryTarget(args.target)) {
    args.onBeforeProcessingRetry?.();
    const result = await args.retryDocumentProcessing(args.target);
    return { path: 'processing', result };
  }
  args.onReupload();
  return { path: 'reupload' };
}

/** Last known collection from upload progress callbacks (post-accept). */
export function resolveFlowDocumentProcessingRetryTarget(args: {
  error?: unknown;
  fromProgress?: FlowDocumentProcessingRetryTarget | null;
}): FlowDocumentProcessingRetryTarget | null {
  const { error, fromProgress } = args;
  if (error instanceof FlowDocumentProcessingError) {
    if (error.state === 'needs_reupload') {
      return null;
    }
    return {
      flowId: error.flowId,
      collectionId: error.collectionId,
    };
  }
  if (fromProgress?.flowId && fromProgress.collectionId) {
    return fromProgress;
  }
  return null;
}

export function hasFlowDocumentProcessingRetryTarget(
  target: FlowDocumentProcessingRetryTarget | null | undefined
): target is FlowDocumentProcessingRetryTarget {
  return Boolean(target?.flowId && target?.collectionId);
}

/** First failed batch row's processing error, else progress fallback (EB, templates). */
export function resolveFlowDocumentProcessingRetryTargetFromUploadBatch(args: {
  items: UploadFlowDocumentBatchItem[];
  fromProgress?: FlowDocumentProcessingRetryTarget | null;
}): FlowDocumentProcessingRetryTarget | null {
  const failedItem = args.items.find(item => item.status === 'failed');
  return resolveFlowDocumentProcessingRetryTarget({
    error: failedItem?.processingError ?? failedItem?.originalError,
    fromProgress: args.fromProgress,
  });
}
